"use client";

import {
  TOOLTIP_CONTENT_APP_CLASS_NAME,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@v1/ui/tooltip";
import type * as React from "react";

type TooltipContentProps = React.ComponentPropsWithoutRef<
  typeof TooltipContent
>;

interface MiniHintTooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  className?: string;
  side?: TooltipContentProps["side"];
  align?: TooltipContentProps["align"];
  sideOffset?: number;
  /** When set (with `onOpenChange`), the tooltip is fully controlled (e.g. hover-only on a composite control). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Overrides `TooltipProvider` delay (ms). Use on clickable triggers when provider uses `delayDuration={0}`. */
  delayDuration?: number;
  /** Portal target when rendered inside a modal dialog (avoids body-portal focus/tooltip bugs). */
  container?: HTMLElement | null;
}

/** Re-export: same as `@v1/ui/tooltip` `TOOLTIP_CONTENT_APP_CLASS_NAME` (Agent-Chat standard). */
export const MINI_HINT_TOOLTIP_CONTENT_CLASS_NAME =
  TOOLTIP_CONTENT_APP_CLASS_NAME;

/**
 * Standard tiny hover hint used for compact suggestion/action controls.
 * Single source for mini tooltip sizing + positioning (wraps `@v1/ui/tooltip`).
 */
export function MiniHintTooltip({
  content,
  children,
  className,
  side = "top",
  align = "center",
  sideOffset = 6,
  open,
  onOpenChange,
  delayDuration,
  container,
}: MiniHintTooltipProps): React.ReactElement {
  const rootProps =
    open !== undefined
      ? { open, onOpenChange: onOpenChange ?? ((): void => {}) }
      : {};
  const delayProps = delayDuration !== undefined ? { delayDuration } : {};

  return (
    <Tooltip {...rootProps} {...delayProps}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={className}
        container={container}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}
